import PolicyLayout from "../components/PolicyLayout";

const PrivacyPolicyPage = () => {
  return (
    <PolicyLayout title="Privacy Policy" lastUpdated="February 2026">
      <p className="text-base leading-relaxed mb-6">
        This Privacy Policy describes how <strong>theSnugglePrints</strong>{" "}
        ("we", "us", "our") collects, uses, and protects the personal
        information of users ("you", "your") of our website and services,
        including customers based outside India. By using our services, you
        agree to the terms of this policy.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">1. Information We Collect</h3>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li>
          <strong>Personal details:</strong> Name, email address, phone number,
          and shipping address, collected when you place an order or request a
          payment link.
        </li>
        <li>
          <strong>Pet photos &amp; descriptions:</strong> Images and any
          captions/descriptions you upload or share for AI processing. These
          are stored securely and used solely to generate your custom art and
          (where applicable) your photo book or video.
        </li>
        <li>
          <strong>Payment data:</strong> Processed entirely by our payment
          provider (currently Razorpay; PayPal launching soon for international
          checkouts). We do not store your card, UPI, or PayPal credentials on
          our servers.
        </li>
        <li>
          <strong>Technical data:</strong> IP address (used to detect your
          country for currency display), browser type, device information, and
          basic analytics to improve our service.
        </li>
      </ul>

      <h3 className="text-2xl font-bold mt-10 mb-4">2. How We Use Your Data</h3>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li>To generate your custom pet art and fulfil your order</li>
        <li>To produce, print, and ship the product to your address</li>
        <li>To send order confirmations, design drafts, and status updates</li>
        <li>To respond to support queries</li>
        <li>To detect your country and show prices in the appropriate currency</li>
        <li>To comply with applicable laws and regulations</li>
      </ul>

      <h3 className="text-2xl font-bold mt-10 mb-4">3. Sharing of Data</h3>
      <p className="text-base leading-relaxed mb-4">
        We share only the minimum data necessary with trusted service
        providers:
      </p>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li>
          <strong>Payment providers</strong> — for payment processing (PCI DSS
          compliant gateways)
        </li>
        <li>
          <strong>Print &amp; logistics partners</strong> — only the shipping
          details required to print, pack, and deliver your order
        </li>
        <li>
          <strong>AI provider (Google Gemini — Nano Banana)</strong> — to
          generate your custom artwork. Uploaded images are processed by this
          provider per their terms and may be processed on servers located
          outside India.
        </li>
      </ul>
      <p className="text-base leading-relaxed mb-6">
        We <strong>do not sell</strong> your personal data to third parties.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">4. Cross-Border Data Transfers</h3>
      <p className="text-base leading-relaxed mb-6">
        We are based in India and our primary servers and operations are
        located in India. If you are accessing our service from outside India
        (including but not limited to the United States, United Kingdom,
        European Union, or any other country we ship to), your personal data
        will be transferred to and processed in India, and may also be
        processed by our AI provider on servers located outside your country.
        By using our service, you consent to this transfer and processing.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">5. Data Retention</h3>
      <p className="text-base leading-relaxed mb-6">
        Order and shipping data is retained for 7 years per Indian tax and
        audit requirements. Uploaded pet images, descriptions, and generated
        artwork are retained for up to 90 days to enable re-prints and support,
        unless you request earlier deletion.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">6. Data Security</h3>
      <p className="text-base leading-relaxed mb-6">
        All traffic to our site is encrypted via HTTPS (TLS 1.2+). Payments are
        handled by PCI DSS compliant gateways. Access to customer data is
        restricted to authorised personnel only.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">7. Your Rights</h3>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li>Access the personal data we hold about you</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your data (subject to legal obligations)</li>
        <li>Withdraw consent for marketing communication at any time</li>
      </ul>
      <p className="text-base leading-relaxed mb-6">
        To exercise any of these rights, email{" "}
        <strong>contact@thesnuggleprints.com</strong>.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">8. Additional Rights for EU / UK Residents (GDPR / UK GDPR)</h3>
      <p className="text-base leading-relaxed mb-4">
        If you are located in the European Economic Area, the United Kingdom,
        or another jurisdiction with similar data-protection laws, you
        additionally have the right to:
      </p>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li>
          <strong>Erasure</strong> — request deletion of your personal data
          ("right to be forgotten"), subject to our legal record-keeping
          obligations
        </li>
        <li>
          <strong>Data portability</strong> — receive a copy of your data in a
          structured, machine-readable format
        </li>
        <li>
          <strong>Restriction of processing</strong> — request that we limit
          how we use your data
        </li>
        <li>
          <strong>Object to processing</strong> — for purposes based on
          legitimate interests or direct marketing
        </li>
        <li>
          <strong>Lodge a complaint</strong> with your local data-protection
          supervisory authority (e.g., ICO in the UK)
        </li>
      </ul>
      <p className="text-base leading-relaxed mb-6">
        Our legal basis for processing your data is (a) performance of a
        contract (fulfilling your order) and (b) your consent for marketing
        communications, which you may withdraw at any time.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">9. Cookies</h3>
      <p className="text-base leading-relaxed mb-6">
        We use essential cookies to run our checkout and session. We do not
        use third-party advertising cookies.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">10. Children's Privacy</h3>
      <p className="text-base leading-relaxed mb-6">
        Our services are not intended for anyone under 18. We do not knowingly
        collect data from minors.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">11. Changes to This Policy</h3>
      <p className="text-base leading-relaxed mb-6">
        We may update this policy from time to time. The "Last updated" date
        above reflects the most recent changes.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">12. Contact</h3>
      <p className="text-base leading-relaxed mb-6">
        Questions about this policy?
        <br />
        Email: <strong>contact@thesnuggleprints.com</strong>
        <br />
        Address: theSnugglePrints (The Happy Prints Company), Flat 102, UKN Esperanza, Block G, Thubarahalli Extension Road, Whitefield, Bengaluru 560066, Karnataka, India. Udyam Registration No. UDYAM-KR-03-0766778
      </p>
    </PolicyLayout>
  );
};

export default PrivacyPolicyPage;
